'use client';

import { ChevronRight } from '@mui/icons-material';
import { type Theme } from '@mui/material';
import { useFeature } from '@next-box/feature-flags';
import { MovieSortBy, ScreenType, TvSortBy } from '@tmdb-graphql-api/schema/types';
import { useMemo, useState } from 'react';
import { ContinueWatchingCarousel } from '#components/ContinueWatchingCarousel/index.tsx';
import { DiscoverCarousel } from '#components/DiscoverCarousel/index.tsx';
import { FavoritesCarousel } from '#components/FavoritesCarousel/index.tsx';
import { Link } from '#components/Link/index.tsx';
import { ListingPreview } from '#components/ListingPreview/index.tsx';
import { RecentlyWatchedCarousel } from '#components/RecentlyWatchedCarousel/index.tsx';
import { ReviewsDrawerQuery } from '#components/ReviewsDrawer/index.ts';
import { Section, SectionColor } from '#components/Section/index.tsx';
import { ShortcutsChipStack } from '#components/ShortcutsChipStack/index.tsx';
import { TrendingCarousel } from '#components/TrendingTabsCarousel/index.tsx';
import { Watchlist } from '#components/Watchlist/index.tsx';
import { FeatureFlags } from '#enums/featureFlags.ts';
import { Genre } from '#enums/genres.ts';
import { Keyword } from '#enums/keywords.ts';
import { mapScreenFormatTypeToScreenType, mapSectionTypeToScreenType } from '#helpers/enumMappers.ts';
import { disable as disableFullscreenLock } from '#helpers/fullscreenLock.ts';
import { useActivePreview } from '#hooks/useActivePreview.ts';
import { useActiveReviewsDrawer } from '#hooks/useActiveReviewsDrawer.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { tt } from '#i18n/index.ts';
import { useStore } from '#store/useStore.ts';
import { type ActiveVideo } from '#types.ts';
import { PlayerSelectorModal } from '../Modal/PlayerSelectorModal.tsx';

const linkStyles = {
  ':hover': {
    color: ({ palette }: Theme) => palette.grey[600],
  },
  alignItems: 'end',
  color: ({ palette }: Theme) => palette.grey[800],
  display: 'flex',
  textDecoration: 'none',
};

const chevronStyles = {
  height: '1.5em',
  width: '1.5em',
};

const animatedMovieFilters = {
  certificationCountry: 'GB',
  certificationGte: 'U',
  certificationLte: 'PG',
  withGenres: Genre.Animation,
};

const animatedTvFilters = {
  screenedTheatrically: false,
  withGenres: Genre.Animation,
  withoutKeywords: Keyword.Anime,
};

export const HomePage = () => {
  const { uaParser } = useToolbelt();
  const feature = useFeature(FeatureFlags.ExoticPinkPtarmigan);
  const hasContinueWatchingTv = useStore(store => store.hasContinueWatchingTv);
  const hasRecentlyWatched = useStore(store => store.hasRecentlyWatched);
  const hasFavorites = useStore(store => store.hasFavorites);
  const [activeVideo, setActiveVideo] = useState<ActiveVideo | undefined>();
  const [activePreview, setActivePreview] = useActivePreview();
  const [activeReviewsDrawer, setActiveReviewsDrawer] = useActiveReviewsDrawer();

  const memoHome = useMemo(
    () => (
      <>
        <Section title={tt('homepage.sections.trending')}>
          <TrendingCarousel setActivePreview={setActivePreview} setActiveVideo={setActiveVideo} />
        </Section>
        <Watchlist setActivePreview={setActivePreview}>
          {watchlistWidget =>
            watchlistWidget ? (
              <Section color={SectionColor.Grey} title={tt('categories.watchlist')}>
                {watchlistWidget}
              </Section>
            ) : undefined
          }
        </Watchlist>
        <DiscoverCarousel
          initiator="PopularMoviesCarousel"
          setActivePreview={setActivePreview}
          sortBy={MovieSortBy.PopularityDesc}
          type={ScreenType.Movie}
        >
          {discoverCarousel =>
            discoverCarousel ? (
              <Section
                title={
                  <Link sx={linkStyles} to="/discover/movies">
                    {tt('homepage.sections.popularMovies')}
                    <ChevronRight sx={chevronStyles} />
                  </Link>
                }
              >
                {discoverCarousel}
              </Section>
            ) : undefined
          }
        </DiscoverCarousel>
        <DiscoverCarousel
          initiator="PopularTvShowsCarousel"
          setActivePreview={setActivePreview}
          sortBy={TvSortBy.PopularityDesc}
          type={ScreenType.Tv}
        >
          {discoverCarousel =>
            discoverCarousel ? (
              <Section
                title={
                  <Link sx={linkStyles} to="/discover/tv">
                    {tt('homepage.sections.popularTv')}
                    <ChevronRight sx={chevronStyles} />
                  </Link>
                }
              >
                {discoverCarousel}
              </Section>
            ) : undefined
          }
        </DiscoverCarousel>
        {feature && hasContinueWatchingTv() ? (
          <ContinueWatchingCarousel setActivePreview={setActivePreview}>
            {continueWatchingCarousel =>
              continueWatchingCarousel ? (
                <Section color={SectionColor.Grey} title={tt('categories.continueWatching')}>
                  {continueWatchingCarousel}
                </Section>
              ) : undefined
            }
          </ContinueWatchingCarousel>
        ) : undefined}
        <Section color={SectionColor.DarkGrey} title={tt('categories.shortcuts')}>
          <ShortcutsChipStack invertColor />
        </Section>
        <DiscoverCarousel
          filters={animatedMovieFilters}
          initiator="PopularAnimatedMoviesCarousel"
          setActivePreview={setActivePreview}
          sortBy={MovieSortBy.PopularityDesc}
          type={ScreenType.Movie}
        >
          {discoverCarousel =>
            discoverCarousel ? (
              <Section
                title={
                  <Link
                    sx={linkStyles}
                    to='/discover/movies?discoverFilters=%7B"certificationCountry"%3A"GB"%2C"certificationGte"%3A"U"%2C"certificationLte"%3A"PG"%2C"withGenres"%3A"16"%7D'
                  >
                    {tt('homepage.sections.popularAnimatedMovies')}
                    <ChevronRight sx={chevronStyles} />
                  </Link>
                }
              >
                {discoverCarousel}
              </Section>
            ) : undefined
          }
        </DiscoverCarousel>
        <DiscoverCarousel
          filters={animatedTvFilters}
          initiator="PopularAnimatedTvShowsCarousel"
          setActivePreview={setActivePreview}
          sortBy={MovieSortBy.PopularityDesc}
          type={ScreenType.Tv}
        >
          {discoverCarousel =>
            discoverCarousel ? (
              <Section
                title={
                  <Link
                    sx={linkStyles}
                    to='/discover/tv?&discoverFilters=%7B"screenedTheatrically"%3Afalse%2C"withGenres"%3A"16"%2C"withoutKeywords"%3A"210024"%7D'
                  >
                    {tt('homepage.sections.popularAnimatedTv')}
                    <ChevronRight sx={chevronStyles} />
                  </Link>
                }
              >
                {discoverCarousel}
              </Section>
            ) : undefined
          }
        </DiscoverCarousel>
        {feature && hasRecentlyWatched() ? (
          <RecentlyWatchedCarousel setActivePreview={setActivePreview}>
            {recentlyWatchedCarousel =>
              recentlyWatchedCarousel ? (
                <Section color={SectionColor.Grey} title={tt('categories.recentlyWatched')}>
                  {recentlyWatchedCarousel}
                </Section>
              ) : undefined
            }
          </RecentlyWatchedCarousel>
        ) : undefined}
        <DiscoverCarousel
          initiator="TopRatedMoviesCarousel"
          setActivePreview={setActivePreview}
          sortBy={MovieSortBy.VoteAverageDesc}
          type={ScreenType.Movie}
        >
          {discoverCarousel =>
            discoverCarousel ? (
              <Section
                title={
                  <Link sx={linkStyles} to="/discover/movies?discoverSortBy=VOTE_AVERAGE_DESC">
                    {tt('homepage.sections.topRatedMovies')}
                    <ChevronRight sx={chevronStyles} />
                  </Link>
                }
              >
                {discoverCarousel}
              </Section>
            ) : undefined
          }
        </DiscoverCarousel>
        <DiscoverCarousel
          initiator="TopRatedTvShowsCarousel"
          setActivePreview={setActivePreview}
          sortBy={TvSortBy.VoteAverageDesc}
          type={ScreenType.Tv}
        >
          {discoverCarousel =>
            discoverCarousel ? (
              <Section
                title={
                  <Link sx={linkStyles} to="/discover/tv?&discoverSortBy=VOTE_AVERAGE_DESC">
                    {tt('homepage.sections.topRatedTv')}
                    <ChevronRight sx={chevronStyles} />
                  </Link>
                }
              >
                {discoverCarousel}
              </Section>
            ) : undefined
          }
        </DiscoverCarousel>
        <DiscoverCarousel
          filters={animatedMovieFilters}
          initiator="TopRatedAnimatedMoviesCarousel"
          setActivePreview={setActivePreview}
          sortBy={MovieSortBy.VoteAverageDesc}
          type={ScreenType.Movie}
        >
          {discoverCarousel =>
            discoverCarousel ? (
              <Section
                title={
                  <Link
                    sx={linkStyles}
                    to='/discover/movies?discoverSortBy=VOTE_AVERAGE_DESC&discoverFilters=%7B"certificationCountry"%3A"GB"%2C"certificationGte"%3A"U"%2C"certificationLte"%3A"PG"%2C"withGenres"%3A"16"%7D'
                  >
                    {tt('homepage.sections.topRatedAnimatedMovies')}
                    <ChevronRight sx={chevronStyles} />
                  </Link>
                }
              >
                {discoverCarousel}
              </Section>
            ) : undefined
          }
        </DiscoverCarousel>
        <DiscoverCarousel
          filters={animatedTvFilters}
          initiator="TopRatedAnimatedTvCarousel"
          setActivePreview={setActivePreview}
          sortBy={MovieSortBy.VoteAverageDesc}
          type={ScreenType.Tv}
        >
          {discoverCarousel =>
            discoverCarousel ? (
              <Section
                title={
                  <Link
                    sx={linkStyles}
                    to='/discover/tv?&discoverFilters=%7B"screenedTheatrically"%3Afalse%2C"withGenres"%3A"16"%2C"withoutKeywords"%3A"210024"%7D&discoverSortBy=VOTE_AVERAGE_DESC'
                  >
                    {tt('homepage.sections.topRatedAnimatedTv')}
                    <ChevronRight sx={chevronStyles} />
                  </Link>
                }
              >
                {discoverCarousel}
              </Section>
            ) : undefined
          }
        </DiscoverCarousel>
        {hasFavorites() ? (
          <FavoritesCarousel setActivePreview={setActivePreview}>
            {favoritesCarousel =>
              favoritesCarousel ? (
                <Section color={SectionColor.Grey} title={tt('categories.favorites')}>
                  {favoritesCarousel}
                </Section>
              ) : undefined
            }
          </FavoritesCarousel>
        ) : undefined}
        <DiscoverCarousel
          filters={{ withGenres: Genre.Documentary, withoutKeywords: Keyword.Nature }}
          initiator="TopRatedDocsCarousel"
          setActivePreview={setActivePreview}
          sortBy={MovieSortBy.VoteAverageDesc}
          type={ScreenType.Movie}
        >
          {discoverCarousel =>
            discoverCarousel ? (
              <Section
                title={
                  <Link
                    sx={linkStyles}
                    to='/discover/movies?discoverSortBy=VOTE_AVERAGE_DESC&discoverFilters=%7B"withGenres"%3A"99"%2C"withoutKeywords"%3A"221355"%7D'
                  >
                    {tt('homepage.sections.topRatedDocs')}
                    <ChevronRight sx={chevronStyles} />
                  </Link>
                }
              >
                {discoverCarousel}
              </Section>
            ) : undefined
          }
        </DiscoverCarousel>
        <DiscoverCarousel
          filters={{ withKeywords: Keyword.Nature }}
          initiator="PopularNatureCarousel"
          setActivePreview={setActivePreview}
          sortBy={MovieSortBy.PopularityDesc}
          type={ScreenType.Tv}
        >
          {discoverCarousel =>
            discoverCarousel ? (
              <Section
                title={
                  <Link
                    sx={linkStyles}
                    to='/discover/tv?&discoverSortBy=POPULARITY_DESC&discoverFilters=%7B"screenedTheatrically"%3Afalse%2C"withKeywords"%3A"221355"%7D'
                  >
                    {tt('homepage.sections.popularNature')}
                    <ChevronRight sx={chevronStyles} />
                  </Link>
                }
              >
                {discoverCarousel}
              </Section>
            ) : undefined
          }
        </DiscoverCarousel>
      </>
    ),
    // We only want to execute this on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feature],
  );

  return (
    <>
      {activePreview ? <ListingPreview setActivePreview={setActivePreview} {...activePreview} /> : undefined}
      {activeVideo?.src && activeVideo.src.length > 0 ? (
        <PlayerSelectorModal
          activeVideo={activeVideo}
          onClose={() => {
            setActiveVideo(undefined);
            void disableFullscreenLock(uaParser);
          }}
          type={mapScreenFormatTypeToScreenType(activeVideo.type)}
        />
      ) : undefined}
      {activeReviewsDrawer ? (
        <ReviewsDrawerQuery
          id={activeReviewsDrawer.id}
          setActiveReviewsDrawer={setActiveReviewsDrawer}
          type={mapSectionTypeToScreenType(activeReviewsDrawer.type)}
        />
      ) : undefined}
      {memoHome}
    </>
  );
};
